import React, { forwardRef } from 'react';
import cx from 'classnames';

// icons
import SearchGlass from '../../../icons/SearchGlass';
import LockIcon from '../../../icons/LockIcons';
import UserIcon from '../../../icons/UserIcons';
import MailIcon from '../../../icons/MailIcon';
import CheckCircle from '../../../icons/CheckCircle';
import ExclamationWarning from '../../../icons/ExclamationWarning';

import classes from './TextField.module.scss';

export interface Props {
  // style will be applied to the topmost element
  style?: any;
  // className will be applied to the actual input rather than the top level element
  className?: string;
  /** Input ID.
   * @default ${randomValue}
   */
  id?: string;
  /** Label to display above input. */
  label?: string;
  /** Input name field. */
  name?: string;
  /** Input needs to be filled out to submit a form. */
  required?: boolean;
  /** Input type.
   * @default text
   */
  // if true, outlines the input in red, the icon turns red, and 'requiredText'
  // will show beneath the input (if provided)
  showRequiredState?: boolean;
  // if 'showRequiredState' is true, requiredText will show as red text beneath the input
  requiredText?: string;
  type?:
    | 'date'
    | 'datetime-local'
    | 'email'
    | 'month'
    | 'number'
    | 'password'
    | 'search'
    | 'tel'
    | 'text'
    | 'time'
    | 'url'
    | 'week';
  // placeholder text in the input
  placeholder?: string;
  // icon shows in the left side of the input
  iconType?: 'searchIcon' | 'lockIcon' | 'mailIcon' | 'userIcon';
  /** Controlled input value. */
  value?: string;
  // If 'isValid' is set to true, a green circle checkmark will show.
  // If set to false, a yellow exclamation circle will show.
  // If unset, nothing will show
  isValid?: boolean;
  // when disabled, the input is grayed out and can't be focused
  disabled?: boolean;
  // same behavior as disabled but with normal background and grey text
  locked?: boolean;
  // sets the width
  width?: number;

  // sets the <input> height
  inputHeight?: number;
  inputStyles?: object;
  // event listeners (we can continue adding these in here as needed to support new functionality)
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => any;
  onFocus?: (e: React.ChangeEvent<HTMLInputElement>) => any;
  onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => any;
}

const TextField = forwardRef(
  (
    {
      style,
      className,
      id,
      label,
      name,
      required,
      showRequiredState,
      requiredText,
      type = 'text',
      placeholder,
      iconType,
      value,
      isValid,
      disabled,
      locked,
      onChange,
      onFocus,
      onBlur,
      inputHeight,
      inputStyles,
    }: Props,
    ref: any
  ) => {
    function getIconFromType(iconType) {
      const fillColor = '#BDBDBD';
      switch (iconType) {
        case 'searchIcon':
          return <SearchGlass lineColor={fillColor} />;
        case 'lockIcon':
          return <LockIcon fillColor={fillColor} />;
        case 'mailIcon':
          return <MailIcon fillColor={fillColor} />;
        case 'userIcon':
          return <UserIcon fillColor={fillColor} />;
        default:
          return null;
      }
    }
    const inputIcon = getIconFromType(iconType);

    let isValidIcon: any = null;
    if (isValid === true) isValidIcon = <CheckCircle />;
    if (isValid === false) isValidIcon = <ExclamationWarning />;

    return (
      <label
        className={cx(classes.textField, { [classes.disabled]: disabled })}
        htmlFor={id}
        style={{ ...style }}
      >
        <div className={classes.label}>{label}</div>
        <div
          className={cx(classes.inputWrapper, {
            [classes.required]: showRequiredState,
            [classes.disabled]: disabled,
          })}
          style={{ height: inputHeight ? `${inputHeight + 4}px` : undefined }}
        >
          {inputIcon && <div className={classes.icon}>{inputIcon}</div>}
          <input
            className={cx(classes.input, className, {
              [classes.disabled]: disabled,
              [classes.locked]: locked,
            })}
            data-context="react"
            ref={ref}
            name={name}
            type={type}
            required={required}
            placeholder={placeholder}
            value={value}
            disabled={disabled || locked}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            style={{
              height: `${inputHeight}px`,
              ...(locked ? { color: '#767b8e' } : {}),
              ...inputStyles,
            }}
          />
          <div className={classes.isValidIcon}>{isValidIcon}</div>
        </div>
        {showRequiredState && (
          <p className={classes.requiredText}>{requiredText}</p>
        )}
      </label>
    );
  }
);

export default TextField;
